/**
 * WallJoint - Wall connection/joint calculations
 *
 * Handles butt joints and miter joints between walls.
 * A wall has 8 corner points (4 bottom + 4 top).
 * Joints modify the 4 points at the connection end.
 */

import { Vector } from '../geometry/base/Vector';

/**
 * Joint types
 */
export const JointType = {
  BUTT: 'butt',     // Inner wall stops at outer wall's inner face
  MITER: 'miter',   // Angle bisector cut
  NONE: 'none',     // No joint (free end)
};

/**
 * Calculate the perpendicular (normal) direction for a wall baseline.
 * Returns the left-hand normal in XY plane.
 * @param {Vector} start - Baseline start
 * @param {Vector} end - Baseline end
 * @returns {Vector} Unit normal pointing to the "outer" side (left of direction)
 */
export function wallNormal(start, end) {
  const dir = end.subtract(start).normalize();
  // Left-hand perpendicular in XY: rotate 90° CCW
  return new Vector(-dir.y, dir.x, 0);
}

/**
 * Calculate the 8 corner points of a wall volume body.
 *
 * The wall is defined by:
 * - A baseline (inner edge) from start to end
 * - A thickness (expands outward from inner edge)
 * - A height (Z direction)
 * - An optional flip flag (reverses outward direction)
 *
 * Point naming:
 *   innerBottomStart, innerBottomEnd   (= baseline at z=0)
 *   outerBottomStart, outerBottomEnd   (= offset by thickness at z=0)
 *   innerTopStart, innerTopEnd         (= baseline at z=height)
 *   outerTopStart, outerTopEnd         (= offset by thickness at z=height)
 *
 * @param {Vector} baseStart - Baseline start point (inner edge)
 * @param {Vector} baseEnd - Baseline end point (inner edge)
 * @param {number} thickness - Wall thickness in mm
 * @param {number} height - Wall height in mm
 * @param {boolean} flipped - If true, thickness expands to the other side
 * @returns {Object} Object with 8 named Vector points
 */
export function calculateWallPoints(baseStart, baseEnd, thickness, height, flipped = false) {
  const normal = wallNormal(baseStart, baseEnd);
  const offset = normal.multiply(flipped ? -thickness : thickness);

  const innerBottomStart = new Vector(baseStart.x, baseStart.y, 0);
  const innerBottomEnd = new Vector(baseEnd.x, baseEnd.y, 0);
  const outerBottomStart = innerBottomStart.add(offset);
  const outerBottomEnd = innerBottomEnd.add(offset);

  const zUp = new Vector(0, 0, height);
  const innerTopStart = innerBottomStart.add(zUp);
  const innerTopEnd = innerBottomEnd.add(zUp);
  const outerTopStart = outerBottomStart.add(zUp);
  const outerTopEnd = outerBottomEnd.add(zUp);

  return {
    innerBottomStart,
    innerBottomEnd,
    outerBottomStart,
    outerBottomEnd,
    innerTopStart,
    innerTopEnd,
    outerTopStart,
    outerTopEnd,
  };
}

/**
 * Calculate butt joint: trim one wall so it stops at the other wall's inner face.
 *
 * The "trimmed" wall's end points (inner + outer at that end) are projected
 * onto the "target" wall's inner face line.
 *
 * @param {Object} trimmedWall - Wall data { baseStart, baseEnd, thickness, flipped }
 * @param {Object} targetWall - Wall data to stop against
 * @param {'start'|'end'} trimmedEnd - Which end of the trimmed wall to adjust
 * @returns {Object} New positions for the 4 points at the trimmed end { innerBottom, outerBottom, innerTop, outerTop }
 */
export function calculateButtJoint(trimmedWall, targetWall, trimmedEnd = 'end') {
  // Target wall inner face line
  const targetStart = targetWall.baseStart;
  const targetEnd = targetWall.baseEnd;
  const targetDir = targetEnd.subtract(targetStart).normalize();

  // Trimmed wall baseline direction
  const trimDir = trimmedWall.baseEnd.subtract(trimmedWall.baseStart).normalize();
  const trimNormal = wallNormal(trimmedWall.baseStart, trimmedWall.baseEnd);
  const offset = trimNormal.multiply(trimmedWall.flipped ? -trimmedWall.thickness : trimmedWall.thickness);

  // The point we're trimming
  const trimPoint = trimmedEnd === 'start' ? trimmedWall.baseStart : trimmedWall.baseEnd;

  // Project trimmed wall's inner point onto target wall's inner face line
  // Find intersection of trimmed wall baseline (extended) with target inner face
  const innerIntersection = lineLineIntersection2D(
    trimmedWall.baseStart, trimmedWall.baseEnd,
    targetStart, targetEnd
  );

  if (!innerIntersection) {
    // Parallel walls — no intersection, return original points
    return null;
  }

  // Outer point: offset the inner intersection by wall thickness
  const outerIntersection = innerIntersection.add(offset);

  const height = trimmedWall.height || 2750;
  const zUp = new Vector(0, 0, height);

  return {
    innerBottom: innerIntersection,
    outerBottom: outerIntersection,
    innerTop: innerIntersection.add(zUp),
    outerTop: outerIntersection.add(zUp),
  };
}

/**
 * Calculate miter joint: angle bisector cut between two walls.
 *
 * Both walls' end points are extended/trimmed to the miter line.
 *
 * @param {Object} wall1 - First wall data
 * @param {Object} wall2 - Second wall data
 * @param {'start'|'end'} wall1End - Connection end on wall1
 * @param {'start'|'end'} wall2End - Connection end on wall2
 * @returns {Object} { wall1Points, wall2Points } - New corner positions for both walls at the joint
 */
export function calculateMiterJoint(wall1, wall2, wall1End = 'end', wall2End = 'start') {
  const dir1 = wall1.baseEnd.subtract(wall1.baseStart).normalize();
  const dir2 = wall2.baseEnd.subtract(wall2.baseStart).normalize();

  // If wall1End is 'start', we use the reverse direction for the bisector
  const effectiveDir1 = wall1End === 'start' ? dir1.negate() : dir1;
  const effectiveDir2 = wall2End === 'start' ? dir2 : dir2.negate();

  // Find intersection of inner baselines
  const innerIntersection = lineLineIntersection2D(
    wall1.baseStart, wall1.baseEnd,
    wall2.baseStart, wall2.baseEnd
  );

  if (!innerIntersection) return null;

  // Find intersection of outer baselines
  const normal1 = wallNormal(wall1.baseStart, wall1.baseEnd);
  const offset1 = normal1.multiply(wall1.flipped ? -wall1.thickness : wall1.thickness);
  const normal2 = wallNormal(wall2.baseStart, wall2.baseEnd);
  const offset2 = normal2.multiply(wall2.flipped ? -wall2.thickness : wall2.thickness);

  const outerIntersection = lineLineIntersection2D(
    wall1.baseStart.add(offset1), wall1.baseEnd.add(offset1),
    wall2.baseStart.add(offset2), wall2.baseEnd.add(offset2)
  );

  const height1 = wall1.height || 2750;
  const height2 = wall2.height || 2750;
  const zUp1 = new Vector(0, 0, height1);
  const zUp2 = new Vector(0, 0, height2);

  return {
    wall1Points: {
      innerBottom: innerIntersection,
      outerBottom: outerIntersection || innerIntersection.add(offset1),
      innerTop: innerIntersection.add(zUp1),
      outerTop: (outerIntersection || innerIntersection.add(offset1)).add(zUp1),
    },
    wall2Points: {
      innerBottom: innerIntersection,
      outerBottom: outerIntersection || innerIntersection.add(offset2),
      innerTop: innerIntersection.add(zUp2),
      outerTop: (outerIntersection || innerIntersection.add(offset2)).add(zUp2),
    },
  };
}

/**
 * Detect wall connections: find walls that share baseline endpoints.
 *
 * @param {Array} walls - Array of wall data objects
 * @param {number} tolerance - Snap tolerance in mm
 * @returns {Array} Array of { wall1Id, wall1End, wall2Id, wall2End, jointType }
 */
export function detectWallConnections(walls, tolerance = 1) {
  const connections = [];

  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const w1 = walls[i];
      const w2 = walls[j];

      const endpoints = [
        { w1End: 'start', w2End: 'start', p1: w1.baseStart, p2: w2.baseStart },
        { w1End: 'start', w2End: 'end', p1: w1.baseStart, p2: w2.baseEnd },
        { w1End: 'end', w2End: 'start', p1: w1.baseEnd, p2: w2.baseStart },
        { w1End: 'end', w2End: 'end', p1: w1.baseEnd, p2: w2.baseEnd },
      ];

      for (const ep of endpoints) {
        const dist = Math.sqrt(
          (ep.p1.x - ep.p2.x) ** 2 + (ep.p1.y - ep.p2.y) ** 2
        );
        if (dist <= tolerance) {
          connections.push({
            wall1Id: w1.id,
            wall1End: ep.w1End,
            wall2Id: w2.id,
            wall2End: ep.w2End,
            jointType: JointType.BUTT, // Default
          });
          break; // Only one connection per pair
        }
      }
    }
  }

  return connections;
}

/**
 * 2D line-line intersection
 * @param {Vector} p1 - Line 1 start
 * @param {Vector} p2 - Line 1 end
 * @param {Vector} p3 - Line 2 start
 * @param {Vector} p4 - Line 2 end
 * @returns {Vector|null} Intersection point or null if parallel
 */
function lineLineIntersection2D(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return null;

  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / cross;

  return new Vector(
    p1.x + t * d1x,
    p1.y + t * d1y,
    0
  );
}

export default {
  JointType,
  wallNormal,
  calculateWallPoints,
  calculateButtJoint,
  calculateMiterJoint,
  detectWallConnections,
};
